import React, { useState, useEffect, useMemo } from 'react'
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'
import { useJsonLd } from '../hooks/useJsonLd'
import { buildJobPostingGraph } from '../lib/schema'
import { getJobBySlug, getAllJobs } from '../utils/careers'
import JobApplicationForm from '../components/JobApplicationForm'
import { Button } from '@/components/ui/button'

const JobDetail = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const t = useTranslation()
  const { isLoading } = useLanguage()
  const [job, setJob] = useState(null)
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Scroll to top when component mounts or slug changes
    window.scrollTo(0, 0)
    loadJob()
  }, [slug])

  const loadJob = async () => {
    try {
      setLoading(true)
      setError(null)
      const [jobData, allJobs] = await Promise.all([
        getJobBySlug(slug),
        getAllJobs()
      ])
      
      if (!jobData) {
        setError('Job not found')
        return
      }
      
      setJob(jobData)
      setJobs(allJobs)
    } catch (err) {
      console.error('Error loading job:', err)
      setError('Failed to load job description')
    } finally {
      setLoading(false)
    }
  }

  const jobDescriptionPlain = useMemo(() => {
    if (!job?.rawContent) return ''
    return String(job.rawContent)
      .replace(/[#*`_[\]()>]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }, [job?.rawContent])

  const jobMetaDescription =
    jobDescriptionPlain.length > 0
      ? `${jobDescriptionPlain.slice(0, 155)}${jobDescriptionPlain.length > 155 ? '…' : ''}`
      : t('careers.meta.description')

  usePageSeo({
    enabled: !isLoading && !loading && !!job,
    title: `${job?.title || slug} | VIVA Psychotherapy`,
    description: jobMetaDescription,
    keywords: t('careers.meta.keywords'),
    pathname: location.pathname
  })

  const jobSchema = useMemo(() => {
    if (!job) return null
    return buildJobPostingGraph({
      job,
      pathname: location.pathname,
      description: jobMetaDescription
    })
  }, [job, location.pathname, jobMetaDescription])

  useJsonLd('data-job-schema', jobSchema, !isLoading && !loading && !!job)

  // Show loading state if translations are still loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite">
        <main>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded mb-4"></div>
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-offWhite">
        <main>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded mb-4 w-1/3"></div>
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-2/3"></div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-primary-offWhite">
        <main>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('careers.jobDetail.notFound.title')}</h1>
              <p className="text-gray-600 mb-6">{error || t('careers.jobDetail.notFound.message')}</p>
              <Button asChild variant="brand">
                <Link to="/careers">Back to Careers</Link>
              </Button>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-offWhite">
      <main>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button */}
          <Link
            to="/careers"
            className="inline-flex items-center gap-2 text-primary-darkGreen hover:text-primary-darkGreen/80 mb-8"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('careers.jobDetail.backToCareers')}
          </Link>

          {/* Job Header */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-primary-darkGreen mb-6">
              {job.title || slug}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 mb-6">
              {job.location && (
                <div className="flex items-center gap-2 text-gray-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{job.location}</span>
                </div>
              )}
              {job.type && (
                <div className="flex items-center gap-2 text-gray-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>{job.type}</span>
                </div>
              )}
              {job.experience && (
                <div className="flex items-center gap-2 text-gray-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span>{job.experience}</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-4">
              <Button asChild variant="brand">
                <a href="#application-form">{t('careers.jobDetail.applyNow')}</a>
              </Button>
              <Button asChild variant="secondary">
                <Link to="/careers">{t('careers.jobDetail.viewAllPositions')}</Link>
              </Button>
            </div>
          </div>

          {/* Job Content */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div 
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: job.content }}
            />
          </div>

          {/* Application Form */}
          <div id="application-form" className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-primary-darkGreen mb-4">
              {t('careers.jobDetail.readyToApply.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('careers.jobDetail.readyToApply.description')}
            </p>
            <JobApplicationForm jobTitle={job.title || slug} jobs={jobs} />
          </div>
        </div>
      </main>
    </div>
  )
}

export default JobDetail

